/**
 * Unified inventory synchronization hook
 * 
 * Coordinates all refresh mechanisms to keep inventory and collections in sync:
 * 1. Periodic background polling (45s) - for shared list updates
 * 2. App resume detection - refresh after returning from background
 * 3. Network recovery - auto-refresh when network reconnects
 * 4. Health checks - ensure collections never stay empty when network is available
 * 
 * This is especially important for shared shopping lists where multiple users
 * need to stay in sync.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useAppResume } from './useAppResume';
import { useNetworkStatus } from './useNetworkStatus';
import { recordNetworkFailure, recordNetworkSuccess } from './useNetworkRecovery';
import { useCollectionChangeDetector } from './useCollectionChangeDetector';
import { useAuth } from '../contexts/AuthContext';

interface UseInventorySyncOptions {
  /** Callback to refresh inventory items */
  onRefreshItems?: () => void | Promise<void>;
  /** Callback to refresh collections */
  onRefreshCollections?: () => void | Promise<void>;
  /** Callback to refresh counts */
  onRefreshCounts?: () => void | Promise<void>;
  /** Whether this component is currently active/visible */
  isActive?: boolean;
  /** Current collection count (to detect empty state) */
  collectionsCount?: number;
  /** Polling interval in milliseconds (default: 45000 = 45s) */
  pollingInterval?: number;
}

export const useInventorySync = ({
  onRefreshItems,
  onRefreshCollections,
  onRefreshCounts,
  isActive = true,
  collectionsCount = 0,
  pollingInterval = 45000,
}: UseInventorySyncOptions) => {
  const { token } = useAuth();
  const { isOnline } = useNetworkStatus();
  const { checkForChanges } = useCollectionChangeDetector();
  const wasOfflineRef = useRef(false);
  const healthCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<number>(0);
  const consecutiveFailuresRef = useRef<number>(0);
  const lastFailureTimeRef = useRef<number>(0);
  const lastCollectionActivityRef = useRef<string | null>(null);

  // Smart refresh function that checks collection activity first
  const refreshAll = useCallback(async (silent = true, forceCollections = false) => {
    const now = Date.now();
    
    // Debounce: don't refresh more than once per 5 seconds
    if (now - lastRefreshRef.current < 5000) {
      console.log('[InventorySync] Skipping refresh (debounced)');
      return;
    }
    
    lastRefreshRef.current = now;
    
    if (!silent) {
      console.log('[InventorySync] Refreshing all data...');
    }
    
    try {
      let shouldRefreshCollections = forceCollections;
      
      // Check if collections have changed (lightweight ~1ms query)
      if (!forceCollections && token) {
        try {
          const { hasChanges, latestActivity } = await checkForChanges(
            token,
            lastCollectionActivityRef.current
          );
          
          if (hasChanges) {
            console.log('[InventorySync] Collection changes detected, will refresh');
            shouldRefreshCollections = true;
            lastCollectionActivityRef.current = latestActivity;
          } else {
            console.log('[InventorySync] No collection changes, skipping collection refresh');
          }
        } catch (error) {
          // If activity check fails, refresh collections anyway to be safe
          console.warn('[InventorySync] Activity check failed, will refresh collections:', error);
          shouldRefreshCollections = true;
        }
      }
      
      // Refresh data in parallel (skip collections if no changes)
      const refreshPromises = [
        onRefreshItems?.(),
        onRefreshCounts?.(),
      ];
      
      if (shouldRefreshCollections) {
        refreshPromises.push(onRefreshCollections?.());
      }
      
      await Promise.all(refreshPromises);
      
      // Success - reset failure counter
      consecutiveFailuresRef.current = 0;
      lastFailureTimeRef.current = 0;
      
      // Record success for network recovery tracking
      recordNetworkSuccess();
    } catch (error) {
      console.error('[InventorySync] Refresh failed:', error);
      
      // Track consecutive failures
      consecutiveFailuresRef.current++;
      lastFailureTimeRef.current = now;
      
      // Record failure for network recovery tracking
      recordNetworkFailure();
    }
  }, [onRefreshItems, onRefreshCollections, onRefreshCounts, token, checkForChanges]);

  // 1. PERIODIC BACKGROUND POLLING
  // Polls every 45 seconds when tab is active to catch shared list updates
  // Uses adaptive retry: quick retry (5s) after first failure for brief outages
  useEffect(() => {
    if (!isActive || !isOnline) {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      return;
    }

    const startPolling = () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
      }

      // Adaptive interval based on recent failures
      // If we just had a failure, retry quickly (5s) in case it was brief
      // Otherwise use normal interval (45s)
      const now = Date.now();
      const timeSinceLastFailure = now - lastFailureTimeRef.current;
      const recentFailure = timeSinceLastFailure < 10000; // Within last 10 seconds
      const hasFailures = consecutiveFailuresRef.current > 0;
      
      let interval = pollingInterval; // Default 45s
      
      if (hasFailures && recentFailure && consecutiveFailuresRef.current === 1) {
        // First failure - retry quickly for brief outages
        interval = 5000; // 5 seconds
        console.log('[InventorySync] Quick retry after first failure (5s)');
      } else if (hasFailures) {
        // Multiple failures - use normal interval
        console.log('[InventorySync] Continuing with normal interval after failures');
      } else {
        console.log('[InventorySync] Starting periodic polling (45s interval)');
      }
      
      pollingTimerRef.current = setInterval(() => {
        if (!document.hidden && isActive && isOnline) {
          console.log('[InventorySync] Periodic refresh');
          refreshAll(true).then(() => {
            // After refresh, restart polling with potentially different interval
            startPolling();
          });
        }
      }, interval);
    };

    startPolling();

    return () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [isActive, isOnline, pollingInterval, refreshAll]);

  // 2. APP RESUME DETECTION
  // Refreshes when app returns from background (30+ seconds)
  // Force collection refresh since app state may be stale
  useAppResume(
    useCallback(() => {
      console.log('[InventorySync] App resumed from background, refreshing all');
      refreshAll(false, true); // Force collections refresh
    }, [refreshAll]),
    30000 // Trigger if backgrounded for 30+ seconds
  );

  // 3. NETWORK RECOVERY
  // Auto-refresh when network reconnects after being offline
  // Force collection refresh since network state may have been corrupted
  useEffect(() => {
    if (isOnline && wasOfflineRef.current) {
      console.log('[InventorySync] Network reconnected, refreshing all');
      wasOfflineRef.current = false;
      
      // Small delay to let network stabilize
      setTimeout(() => {
        refreshAll(false, true); // Force collections refresh
      }, 1000);
    } else if (!isOnline) {
      wasOfflineRef.current = true;
      console.log('[InventorySync] Network offline detected');
    }
  }, [isOnline, refreshAll]);

  // 4. HEALTH CHECK
  // Ensures collections never stay empty when network is available
  // Checks every 30 seconds if collections are empty and network is online
  useEffect(() => {
    if (!isActive) {
      if (healthCheckTimerRef.current) {
        clearInterval(healthCheckTimerRef.current);
        healthCheckTimerRef.current = null;
      }
      return;
    }

    // Initial health check
    if (collectionsCount === 0 && isOnline) {
      console.log('[InventorySync] Health check: collections empty with network online, refreshing');
      refreshAll(false, true); // Force collections refresh
    }

    // Periodic health check every 30 seconds
    healthCheckTimerRef.current = setInterval(() => {
      if (collectionsCount === 0 && isOnline && !document.hidden) {
        console.log('[InventorySync] Health check: collections still empty, refreshing');
        refreshAll(false, true); // Force collections refresh
      }
    }, 30000);

    return () => {
      if (healthCheckTimerRef.current) {
        clearInterval(healthCheckTimerRef.current);
        healthCheckTimerRef.current = null;
      }
    };
  }, [isActive, collectionsCount, isOnline, refreshAll]);

  return {
    /** Manually trigger a refresh */
    refresh: refreshAll,
    /** Current network status */
    isOnline,
  };
};

export default useInventorySync;
